import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { useOnline } from "@/lib/useOnline";
import styles from "./RefreshButton.module.css";

/**
 * Compact refresh control (issue #226).
 *
 * The lessons list is cached by react-query, so a booking made by another
 * student/teacher can leave a stale `spots_left` on screen. This button
 * refetches the list on demand; its glyph spins while the request is in
 * flight and it is disabled while offline.
 *
 * Icon-only (with an accessible label + native tooltip) so it can sit in
 * a header or toolbar without crowding the surrounding controls.
 */
export function RefreshButton({
  onClick,
  refreshing,
  testId = "refresh-lessons",
}: {
  onClick: () => void;
  refreshing: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  const disabled = refreshing || !online;
  const label = refreshing ? t("common.refreshing") : t("common.refresh");

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={!online ? t("offline.bookingDisabled") : label}
      data-testid={testId}
      className={styles.button}
    >
      <span className={refreshing ? styles.spinning : undefined}>
        <Icon name="refresh" size={16} />
      </span>
    </button>
  );
}
